import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Text, Button, Stack, HStack, Icon, Badge, Image, Spinner } from "@chakra-ui/react";
import { ExternalLink, FileText, MapPin, CalendarClock, CheckCircle2, Clock, ShieldAlert } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { Modal } from "@/components/Modal";
import { SignaturePad } from "@/components/SignaturePad";
import { showToast } from "@/components/AppToaster";
import { queueSignoff } from "@/lib/offline";
import { AnswersSections } from "@/components/submissions/AnswersView";
import { colors } from "@/theme/colors";

const dataUrlToBlob = async (d) => (await fetch(d)).blob();
const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtTs = (d) => (d ? new Date(d).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : null);

export const ACK_META = {
  assigned: { bg: "#E6F5F7", color: "#0E7C8B", label: "Assigned" },
  awaiting_signatures: { bg: "#FBEEDA", color: "#8A5106", label: "Awaiting signatures" },
  completed: { bg: "#D7F0EC", color: "#0F6E56", label: "Completed" },
  overdue: { bg: "#FCEBEB", color: "#A32D2D", label: "Overdue" },
  superseded: { bg: "#ECECEC", color: "#6B7280", label: "Superseded" },
};
export const ackStatus = (a) =>
  a.status === "superseded" ? "superseded"
    : a.status !== "completed" && a.due_date < new Date().toISOString().slice(0, 10) ? "overdue" : a.status;
export const signedCounts = (a) => {
  const list = a.assignment_signoffs || [];
  return { signed: list.filter((s) => s.status === "signed").length, total: list.length };
};

// Signature progress list — shared by Submission detail & Acknowledgement detail.
export function SignerList({ signoffs, showSignatures = false }) {
  const [urls, setUrls] = useState({});
  useEffect(() => {
    if (!showSignatures) return;
    const withPath = (signoffs || []).filter((s) => s.signature_path);
    if (!withPath.length) return;
    Promise.all(withPath.map((s) => supabase.storage.from("submission-photos").createSignedUrl(s.signature_path, 300)))
      .then((results) => {
        const out = {};
        withPath.forEach((s, i) => { if (results[i].data?.signedUrl) out[s.signature_path] = results[i].data.signedUrl; });
        setUrls(out);
      });
  }, [signoffs, showSignatures]);

  return (
    <Stack gap="2.5" data-testid="signer-list">
      {(signoffs || []).map((s) => (
        <Flex key={s.id} justify="space-between" align="center" gap="3" flexWrap="wrap" data-testid={`signer-row-${s.id}`}>
          <Box>
            <Text fontSize="sm" fontWeight="600" color={colors.textPrimary}>{s.worker_name}</Text>
            <Text fontSize="xs" color={colors.textSecondary}>{s.signer_role === "assignee" ? "Assignee" : "Additional required signer"}</Text>
          </Box>
          <HStack gap="3">
            {showSignatures && s.signature_path && urls[s.signature_path] && (
              <Image src={urls[s.signature_path]} alt={`${s.worker_name} signature`} maxW="120px" maxH="44px"
                border="1px solid" borderColor={colors.border} borderRadius="8px" bg="white" />
            )}
            {s.status === "signed" ? (
              <HStack gap="1.5">
                <Icon as={CheckCircle2} boxSize="4" color="#0F6E56" />
                <Box textAlign="right">
                  <Badge bg="#D7F0EC" color="#0F6E56" borderRadius="full" px="2.5" data-testid={`signer-status-${s.id}`}>Signed</Badge>
                  <Text fontSize="10px" color={colors.textSecondary}>{fmtTs(s.signed_at)}</Text>
                </Box>
              </HStack>
            ) : (
              <HStack gap="1.5">
                <Icon as={Clock} boxSize="4" color="#8A5106" />
                <Badge bg="#FBEEDA" color="#8A5106" borderRadius="full" px="2.5" data-testid={`signer-status-${s.id}`}>Pending</Badge>
              </HStack>
            )}
          </HStack>
        </Flex>
      ))}
    </Stack>
  );
}

// Loads the resource being acknowledged (if any) — null result means it was deleted.
const useAckResource = (assignment, isResource) => {
  const [resource, setResource] = useState(null);
  const [resourceGone, setResourceGone] = useState(false);
  useEffect(() => {
    if (!isResource) return;
    if (!assignment.resource_id) { setResourceGone(true); return; }
    supabase.from("resources").select("id,title,file_name,file_path").eq("id", assignment.resource_id).maybeSingle()
      .then(({ data }) => (data ? setResource(data) : setResourceGone(true)));
  }, [assignment.resource_id, isResource]);
  return { resource, resourceGone };
};

// Signature capture + upload + RPC sign flow, shared state for the modal.
const useSignSubmit = ({ signoff, assignment, isResource, isAssignee, onDone }) => {
  const { user } = useAuth();
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const sign = async () => {
    if (!sig) { setErr("Please sign before confirming."); return; }
    setBusy(true); setErr(null);
    try {
      const blob = await dataUrlToBlob(sig);
      const path = `${assignment.company_id}/signoffs/${crypto.randomUUID()}.png`;
      const { error: upErr } = await supabase.storage.from("submission-photos").upload(path, blob, { contentType: "image/png" });
      if (upErr) throw upErr;
      const { data, error } = await supabase.rpc("sign_assignment_signoff", { p_signoff: signoff.id, p_signature_path: path });
      if (error) throw error;
      const ver = assignment.resource_version ? ` (v${assignment.resource_version})` : "";
      const when = new Date().toLocaleDateString([], { dateStyle: "medium" });
      showToast(data?.assignment_status === "completed"
        ? (isResource && isAssignee ? `You acknowledged “${assignment.title}”${ver} on ${when}` : "Signature added — all signatures collected")
        : (isResource && isAssignee ? `You acknowledged “${assignment.title}”${ver} on ${when} — awaiting additional signatures` : "Signature added"));
      onDone();
    } catch (e) {
      // Offline: queue the signature exactly like offline form submissions.
      const offline = !navigator.onLine || /failed to fetch|network/i.test(e.message || "");
      if (offline && user) {
        await queueSignoff(user.id, { signoff_id: signoff.id, company_id: assignment.company_id, dataUrl: sig });
        showToast("You're offline — your signature is saved and will send automatically when you reconnect.");
        onDone();
      } else setErr(e.message);
    } finally { setBusy(false); }
  };

  return { sig, setSig, busy, err, setErr, sign };
};

// F1 (16 Sept sweep): loads the completed form being signed. ghost = refuse to sign
// (submission_id NULL, or the row is gone/not visible to this signer). unreachable =
// network failure only (offline) — signing stays allowed via the offline queue.
const useAckSubmission = (assignment, isResource) => {
  const missing = !isResource && !assignment.submission_id;
  const [state, setState] = useState({ loading: !isResource && !missing, sub: null, ghost: missing, unreachable: false });
  useEffect(() => {
    if (isResource || !assignment.submission_id) return;
    supabase.from("submissions")
      .select("id,template_title,worker_name,location_name,created_at,answers,template_schema")
      .eq("id", assignment.submission_id).maybeSingle()
      .then(({ data, error }) => {
        if (error) setState({ loading: false, sub: null, ghost: false, unreachable: true });
        else if (!data) setState({ loading: false, sub: null, ghost: true, unreachable: false });
        else setState({ loading: false, sub: data, ghost: false, unreachable: false });
      })
      .catch(() => setState({ loading: false, sub: null, ghost: false, unreachable: true }));
  }, [assignment.submission_id, isResource]);
  return state;
};

// The "what am I signing" area: document link (resources) or the completed form itself (forms).
function AckDocSection({ isResource, assignment, resource, resourceGone, docAvailable, viewDoc, role, navigate, ack }) {
  if (isResource) {
    if (!docAvailable && resourceGone) {
      return <Text fontSize="sm" color={colors.textSecondary} data-testid="ack-doc-gone">The original document is no longer in the library — your manager may have replaced it.</Text>;
    }
    return (
      <Button variant="outline" onClick={viewDoc} disabled={!docAvailable} data-testid="ack-view-document-btn">
        <ExternalLink size={15} /> View document{resource?.file_name ? ` — ${resource.file_name}` : ""}
      </Button>
    );
  }
  if (ack.ghost) {
    return (
      <Box px="3.5" py="3" borderRadius="10px" bg={colors.criticalBg} data-testid="ack-ghost-refusal">
        <HStack gap="2" align="flex-start">
          <Icon as={ShieldAlert} boxSize="4" color={colors.criticalText} mt="0.5" />
          <Box>
            <Text fontSize="sm" fontWeight="600" color={colors.criticalText}>This sign-off has no completed form attached.</Text>
            <Text fontSize="xs" color={colors.criticalText} mt="1">
              A signature can't be added without reviewing the completed form. Ask your administrator to re-issue this assignment.
            </Text>
          </Box>
        </HStack>
      </Box>
    );
  }
  if (ack.loading) {
    return (
      <Flex align="center" gap="2" py="1" data-testid="ack-submission-loading">
        <Spinner size="sm" color={colors.teal} />
        <Text fontSize="sm" color={colors.textSecondary}>Loading the completed form…</Text>
      </Flex>
    );
  }
  if (ack.unreachable) {
    return (
      <Text fontSize="sm" color={colors.textSecondary} data-testid="ack-offline-note">
        You're offline — the completed form can't be displayed right now. {assignment.worker_name} completed “{assignment.title}”; your signature confirms your review.
      </Text>
    );
  }
  return (
    <Box data-testid="ack-submission-preview">
      <Text fontSize="sm" fontWeight="600" color={colors.textPrimary} mb="1.5">Review what you're signing</Text>
      <Box maxH="300px" overflowY="auto" borderRadius="10px" border="1px solid" borderColor={colors.border} p="3" bg={colors.cream}>
        <AnswersSections sections={ack.sub.template_schema?.sections || []} sub={ack.sub} />
      </Box>
      {["org_admin", "supervisor"].includes(role) && (
        <Button variant="outline" size="sm" mt="2" onClick={() => navigate(`/submissions/${assignment.submission_id}`)} data-testid="ack-view-submission-btn">
          <FileText size={15} /> Open the full form
        </Button>
      )}
    </Box>
  );
}

// View a document / completed form + capture the required signature.
export function AcknowledgeModal({ signoff, assignment, onClose, onDone }) {
  const navigate = useNavigate();
  const { role } = useAuth();
  const isResource = assignment.kind === "resource";
  const isAssignee = signoff.signer_role === "assignee";
  const { resource, resourceGone } = useAckResource(assignment, isResource);
  const ack = useAckSubmission(assignment, isResource);
  const { sig, setSig, busy, err, setErr, sign } = useSignSubmit({ signoff, assignment, isResource, isAssignee, onDone });
  // F1: a form sign-off with nothing behind it cannot be signed (matches the 0010 RPC guard).
  const canSign = isResource || (!ack.ghost && !ack.loading);

  const viewDoc = async () => {
    const path = assignment.resource_file_path || resource?.file_path;
    const { data, error } = await supabase.storage.from("resources").createSignedUrl(path, 300);
    if (error || !data?.signedUrl) { setErr("Could not open the document — please try again."); return; }
    window.open(data.signedUrl, "_blank", "noopener");
  };
  const docAvailable = Boolean(assignment.resource_file_path || resource?.file_path);

  return (
    <Modal open onClose={onClose} title={isResource && isAssignee ? "Acknowledge document" : "Add your signature"} testId="acknowledge-modal">
      <Stack gap="4">
        <Box bg={colors.cream} borderRadius="10px" px="4" py="3">
          <HStack gap="2" flexWrap="wrap">
            <Text fontWeight="700" fontSize="sm" color={colors.textPrimary}>{assignment.title}</Text>
            {assignment.resource_version && (
              <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2" fontSize="10px" data-testid="ack-version-badge">v{assignment.resource_version}</Badge>
            )}
          </HStack>
          <HStack gap="3" flexWrap="wrap" mt="1">
            {assignment.location_name && <HStack gap="1"><Icon as={MapPin} boxSize="3" color={colors.textSecondary} /><Text fontSize="xs" color={colors.textSecondary}>{assignment.location_name}</Text></HStack>}
            <HStack gap="1"><Icon as={CalendarClock} boxSize="3" color={colors.textSecondary} /><Text fontSize="xs" color={colors.textSecondary}>Due {fmtDate(assignment.due_date)}</Text></HStack>
            {!isAssignee && <Text fontSize="xs" color={colors.textSecondary}>Assigned to {assignment.worker_name}</Text>}
          </HStack>
          {assignment.instructions && <Text fontSize="xs" color={colors.textSecondary} mt="1.5" fontStyle="italic">"{assignment.instructions}"</Text>}
        </Box>

        <AckDocSection isResource={isResource} assignment={assignment}
          resource={resource} resourceGone={resourceGone} docAvailable={docAvailable}
          viewDoc={viewDoc} role={role} navigate={navigate} ack={ack} />

        {canSign && (
          <Box>
            <Text fontSize="sm" fontWeight="600" color={colors.textPrimary} mb="1.5">
              {isResource && isAssignee ? "Sign to confirm you've read and understood this document" : "Your signature"}
            </Text>
            <SignaturePad value={sig} onChange={setSig} testId="ack-signature-pad" />
          </Box>
        )}

        {err && <Box px="3" py="2" borderRadius="8px" bg={colors.criticalBg} color={colors.criticalText} fontSize="sm" data-testid="ack-error">{err}</Box>}

        {canSign && (
          <Button onClick={sign} loading={busy} disabled={!sig} data-testid="ack-sign-btn">
            {isResource && isAssignee ? "Sign & acknowledge" : "Sign off"}
          </Button>
        )}
      </Stack>
    </Modal>
  );
}
